import React, { useEffect, useMemo, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import {
    MODEL_CAPABILITY_OPTIONS,
    getCustomProvider,
    normalizeCustomModelConfig,
} from './modelConfig';

const capabilityOptions = MODEL_CAPABILITY_OPTIONS.map((value) => ({ label: value, value }));

function TabPanel({ children, value, index }) {
    if (value !== index) {
        return null;
    }

    return <Box sx={{ pt: 2 }}>{children}</Box>;
}

/**
 * Renders inline custom-model inputs and a modal for beginner/advanced metadata settings.
 */
export default function CustomModelConfigurationPanel({
    provider,
    modelName,
    config,
    disabled = false,
    onModelNameChange,
    onConfigChange,
    title = 'Custom model',
}) {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const providerInfo = getCustomProvider(provider);
    const normalizedConfig = useMemo(() => normalizeCustomModelConfig(config), [config]);
    const modelIdentifierHelperText = providerInfo?.provider === 'vllm'
        ? 'Example: zai-org/GLM-OCR'
        : 'Example: glm-ocr:q8_0';
    const selectedOutputCapability = capabilityOptions.find(
        (option) => option.value === normalizedConfig.model_capabilities.out[0],
    ) || null;

    useEffect(() => {
        if (!providerInfo || normalizedConfig.model_capabilities.out.length <= 1) {
            return;
        }

        onConfigChange({
            ...normalizedConfig,
            model_capabilities: {
                ...normalizedConfig.model_capabilities,
                out: normalizedConfig.model_capabilities.out.slice(0, 1),
            },
        });
    }, [normalizedConfig, onConfigChange, providerInfo]);

    if (!providerInfo) {
        return null;
    }

    const handleMetadataFieldChange = (field, value) => {
        onConfigChange({
            ...normalizedConfig,
            model_metadata: {
                ...normalizedConfig.model_metadata,
                [field]: value,
            },
        });
    };

    return (
        <Box sx={{ mt: 2, p: 2, borderRadius: 2, border: '1px dashed', borderColor: 'divider', bgcolor: 'rgba(15, 23, 42, 0.02)' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
                <Box>
                    <Typography variant="subtitle2">{title}: {providerInfo.label}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {providerInfo.tip}
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Chip size="small" label={`In: ${normalizedConfig.model_capabilities.in.join(', ') || '-'}`} />
                    <Chip size="small" label={`Out: ${normalizedConfig.model_capabilities.out.join(', ') || '-'}`} />
                    <IconButton size="small" onClick={() => setIsDialogOpen(true)} disabled={disabled}>
                        <SettingsIcon fontSize="small" />
                    </IconButton>
                </Stack>
            </Stack>

            <TextField
                sx={{ mt: 2 }}
                fullWidth
                disabled={disabled}
                label={`${providerInfo.label} identifier`}
                placeholder={providerInfo.placeholder}
                value={modelName || ''}
                onChange={(event) => onModelNameChange(event.target.value)}
                helperText={modelIdentifierHelperText}
            />

            <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>{providerInfo.label} settings</DialogTitle>
                <DialogContent dividers>
                    <TextField
                        sx={{ mb: 2 }}
                        fullWidth
                        disabled={disabled}
                        label={`${providerInfo.label} identifier`}
                        placeholder={providerInfo.placeholder}
                        value={modelName || ''}
                        onChange={(event) => onModelNameChange(event.target.value)}
                        helperText={modelIdentifierHelperText}
                    />

                    <Tabs value={activeTab} onChange={(event, value) => setActiveTab(value)}>
                        <Tab label="Beginner" />
                        <Tab label="Advanced" />
                    </Tabs>

                    <TabPanel value={activeTab} index={0}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Autocomplete
                                    multiple
                                    disabled={disabled}
                                    options={capabilityOptions}
                                    value={capabilityOptions.filter((option) => normalizedConfig.model_capabilities.in.includes(option.value))}
                                    onChange={(event, value) => {
                                        onConfigChange({
                                            ...normalizedConfig,
                                            model_capabilities: {
                                                ...normalizedConfig.model_capabilities,
                                                in: value.map((option) => option.value),
                                            },
                                        });
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Input capabilities" />}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Autocomplete
                                    disabled={disabled}
                                    disableClearable
                                    options={capabilityOptions}
                                    value={selectedOutputCapability}
                                    onChange={(event, value) => {
                                        onConfigChange({
                                            ...normalizedConfig,
                                            model_capabilities: {
                                                ...normalizedConfig.model_capabilities,
                                                out: value ? [value.value] : [],
                                            },
                                        });
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Output capabilities" />}
                                />
                            </Grid>
                        </Grid>
                    </TabPanel>

                    <TabPanel value={activeTab} index={1}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Autocomplete
                                    multiple
                                    disabled={disabled}
                                    options={capabilityOptions}
                                    value={capabilityOptions.filter((option) => normalizedConfig.model_capabilities.in.includes(option.value))}
                                    onChange={(event, value) => {
                                        onConfigChange({
                                            ...normalizedConfig,
                                            model_capabilities: {
                                                ...normalizedConfig.model_capabilities,
                                                in: value.map((option) => option.value),
                                            },
                                        });
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Input capabilities" helperText="What the model can accept. Example: OCR models usually keep image here." />}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Autocomplete
                                    disabled={disabled}
                                    disableClearable
                                    options={capabilityOptions}
                                    value={selectedOutputCapability}
                                    onChange={(event, value) => {
                                        onConfigChange({
                                            ...normalizedConfig,
                                            model_capabilities: {
                                                ...normalizedConfig.model_capabilities,
                                                out: value ? [value.value] : [],
                                            },
                                        });
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Output capabilities" helperText="What the model returns. Pick one output capability for now; captioning models usually emit text." />}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Context window"
                                    type="number"
                                    inputProps={{ min: 1, step: 1 }}
                                    value={normalizedConfig.model_metadata.context_window || ''}
                                    helperText="How much prior context the runtime keeps. Typical starting point: 8192."
                                    onChange={(event) => handleMetadataFieldChange('context_window', event.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Temperature"
                                    type="number"
                                    inputProps={{ step: '0.1', min: 0, max: 2 }}
                                    value={normalizedConfig.model_metadata.temperature || ''}
                                    helperText="Lower is more deterministic. A safe default is 0.2."
                                    onChange={(event) => handleMetadataFieldChange('temperature', event.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Top P"
                                    type="number"
                                    inputProps={{ step: '0.05', min: 0, max: 1 }}
                                    value={normalizedConfig.model_metadata.top_p || ''}
                                    helperText="Probability mass cutoff. Typical starting point: 0.95."
                                    onChange={(event) => handleMetadataFieldChange('top_p', event.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Max tokens"
                                    type="number"
                                    inputProps={{ min: 1, step: 1 }}
                                    value={normalizedConfig.model_metadata.max_tokens || ''}
                                    helperText="Maximum generated tokens. Typical starting point: 1024."
                                    onChange={(event) => handleMetadataFieldChange('max_tokens', event.target.value)}
                                />
                            </Grid>
                        </Grid>
                    </TabPanel>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
